import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';

import DataGridWithDynamicXScroll from 'ui-component/data-grids/DataGridWithDynamicXScroll';
import callAzureFunction from 'utils-ts/call-azure-function';
import handleError from 'utils/handle-error';
import { useGetEntityName } from 'utils/entities';

const ScheduledNotificationsDialog = ({ open, setOpen, bookingId }) => {
  const bookingEntityName = useGetEntityName('booking');
  const [loading, setLoading] = useState(false);
  const [bookingConfirmationReminders, setBookingConfirmationReminders] = useState([]);

  const [rowCount, setRowCount] = useState(0);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });
  const [sortModel, setSortModel] = useState([{ field: 'availableAt', sort: 'asc' }]);

  const getBookingConfirmationReminders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await callAzureFunction({ url: 'booking-confirmation-reminders', method: 'get', params: { bookingId } });
      const { rows, count } = response.data;

      setRowCount(count);
      setBookingConfirmationReminders(rows);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  const handleSendConfirmationReminderNow = async (id) => {
    try {
      setLoading(true);
      await callAzureFunction({ url: 'booking-confirmation-reminders/send', method: 'post', data: { id } });
      await getBookingConfirmationReminders();
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirmationReminderNow = async (id) => {
    try {
      setLoading(true);
      await callAzureFunction({ url: 'booking-confirmation-reminders/cancel', method: 'post', data: { id } });

      await getBookingConfirmationReminders();
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: 'schedule',
      headerName: 'Schedule',
      sortable: false,
      flex: 1,
      display: 'flex',
      minWidth: 120,
      renderCell: (params) => {
        const { sendTimeOffset, sendTimeUnit } = params.row.ConfirmationReminder || {};

        const scheduleText =
          sendTimeOffset && sendTimeUnit
            ? `Sends ${sendTimeOffset} ${sendTimeUnit.toLowerCase()}(s) after unconfirmed ${bookingEntityName?.toLowerCase()} is created`
            : 'Immediately';

        return (
          <Typography
            sx={{
              display: 'flex',
              pt: 2,
              height: '100%'
            }}
          >
            {scheduleText}
          </Typography>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: false,
      flex: 0.5,
      minWidth: 120,
      renderCell: (params) => {
        const status = params.row.status?.toLowerCase();

        return (
          <>
            <Typography
              textTransform="capitalize"
              sx={{
                display: 'flex',
                pt: 2,
                height: '100%'
              }}
            >
              {status}
            </Typography>
          </>
        );
      }
    },
    {
      field: 'availableAt',
      headerName: 'Send Date and Time',
      sortable: false,
      flex: 0.5,
      minWidth: 200,
      renderCell: (params) => {
        const { availableAt } = params.row;

        const formattedDate = dayjs(availableAt)
          .tz(dayjs.tz.guess()) // Convert to the user's local timezone
          .format('YYYY-MM-DD hh:mm A');

        return (
          <Typography
            textTransform="capitalize"
            sx={{
              display: 'flex',
              pt: 2,
              height: '100%'
            }}
          >
            {formattedDate}
          </Typography>
        );
      }
    },
    {
      field: 'action',
      headerName: 'Action',
      sortable: false,
      flex: 1,
      minWidth: 250,
      maxWidth: 250,
      renderCell: (params) => {
        const { status } = params.row;

        return (
          <Stack sx={{ height: '100%', py: 1.5 }} alignItems="center">
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => {
                  handleSendConfirmationReminderNow(params.row.id);
                }}
                disabled={loading}
              >
                {status === 'COMPLETED' ? 'Resend' : 'Send Now'}
              </Button>
              {status === 'PENDING' && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    handleCancelConfirmationReminderNow(params.row.id);
                  }}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </Stack>
        );
      }
    }
  ];

  useEffect(() => {
    if (open) {
      getBookingConfirmationReminders();
    }
  }, [getBookingConfirmationReminders, open]);

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle variant="h3">Scheduled Notifications</DialogTitle>
      <DialogContent>
        <Grid item xs={12} sx={{ display: 'flex', height: '100%', overflow: 'auto' }}>
          <DataGridWithDynamicXScroll
            disableColumnMenu
            loading={loading}
            rows={bookingConfirmationReminders}
            columns={columns}
            density="comfortable"
            disableRowSelectionOnClick
            disableColumnFilter
            getRowHeight={() => 'auto'}
            autoHeight
            rowCount={rowCount}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={(newPaginationModel) => setPaginationModel(newPaginationModel)}
            sortModel={sortModel}
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          />
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

ScheduledNotificationsDialog.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  bookingId: PropTypes.string
};

export default ScheduledNotificationsDialog;
